import { FC, useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { t } from "i18next"
import { Box } from "@mui/material"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { PaymentIntentResult, SetupIntentResult, StripePaymentElementOptions } from "@stripe/stripe-js"
import PaymentOrderRepository from "../../../repositories/PaymentOrder/paymentOrder.repository"

import ConfirmationDialog from "../../molecules/ConfirmationDialog/ConfirmationDialog"
import { Route } from "../../../router"
import { logAction, logLandingPageAction } from "../../../clients/logger"
import { errorPopupMessageTemplate, generateErrorCode } from "../../../helpers/logging/templates"
import { LoadingButton } from "@mui/lab"
import useGoogleTagManager from '../../../helpers/analytics/use-google-tag-manager'
import GtmEventName from "../../../helpers/analytics/GtmEventName"

import styles from "./StripePaymentElement.module.css"

export interface StripeModalProps {
    email?: string
    stripePaymentIntentId?: string
    isFirstYearFree: boolean
    isSubscriptionGiftPurchase: boolean
    isSubscriptionGiftRedemption: boolean
    isBundlePurchase: boolean
    consent: boolean
    onConsentFalse: () => void
    onSuccess: () => void
}

export const StripePaymentElement: FC<StripeModalProps> = ({
    email,
    stripePaymentIntentId,
    isFirstYearFree,
    isSubscriptionGiftPurchase,
    isSubscriptionGiftRedemption,
    isBundlePurchase,
    consent,
    onConsentFalse,
    onSuccess,
}) => {
    const stripe = useStripe()
    const elements = useElements()
    const navigate = useNavigate()
    const { trackButtonClick } = useGoogleTagManager()
    const statusCheckTimerRef = useRef<NodeJS.Timeout | null>(null)
    const paymentOrderRepository = new PaymentOrderRepository()

    const [isLoading, setIsLoading] = useState(false)
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
    const [dialogErrorMessageTitle, setDialogErrorMessageTitle] = useState(``)
    const [dialogErrorMessage, setDialogErrorMessage] = useState(``)

    const [retryCount, setRetryCount] = useState(0)

    const handleErrorDialogClose = () => {
        setIsErrorDialogOpen(false)
    }

    // Clean up timer when component unmounts
    useEffect(() => {
        return () => {
            if (statusCheckTimerRef.current) {
                clearTimeout(statusCheckTimerRef.current)
            }
        }
    }, [])

    const startPaymentStatusPolling = (paymentIntentId: string) => {
        if (statusCheckTimerRef.current) {
            clearTimeout(statusCheckTimerRef.current)
        }

        statusCheckTimerRef.current = setTimeout(async () => {
            try {
                logLandingPageAction(`Checking payment status for: ${paymentIntentId}`, `myInfo`)

                const result = await paymentOrderRepository.checkPaymentStatus(paymentIntentId)
                logLandingPageAction(`Payment status for: ${paymentIntentId} received: ${result.status}`, `myInfo`)

                if (result.status === `SUCCESS`) {
                    setIsLoading(false)
                    onSuccess()
                } else if (result.status === `FAILED` || result.status === `CANCELLED`) {
                    setIsLoading(false)
                    setIsErrorDialogOpen(true)
                    setDialogErrorMessage(`Payment was ${result.status.toLowerCase()}. Please try again. Error code: ${generateErrorCode()}`)
                    setDialogErrorMessageTitle(t(`stripe_payment_failure`))
                } else if (result.status === `PENDING` || result.status === `NEW`) {
                    logLandingPageAction(errorPopupMessageTemplate(`Payment still ${result.status.toLowerCase()}, continuing to poll`), `myInfo`)
                    startPaymentStatusPolling(paymentIntentId)
                }
            } catch (error) {
                logLandingPageAction(errorPopupMessageTemplate(`Error checking payment status for paymentId: ${paymentIntentId}, retry count: ${retryCount}, error: ${error}`), `myInfo`)
                // Let the standard Stripe flow continue without showing error to user

                // eslint-disable-next-line no-magic-numbers
                if (retryCount < 3) {
                    setRetryCount(retryCount + 1)
                    startPaymentStatusPolling(paymentIntentId)
                } else {
                    setIsLoading(false)
                    setIsErrorDialogOpen(true)
                    logLandingPageAction(errorPopupMessageTemplate(`Failed to check payment status for paymentId: ${paymentIntentId}, retry count: ${retryCount}`), `myInfo`)
                    setDialogErrorMessage(`Unable to fetch payment status. Please contact NTUC YD Support via youngntuc@ntuc.org.sg. Error code: ${generateErrorCode()}`)
                    setDialogErrorMessageTitle(t(`stripe_payment_failure`))
                }
            }
        // eslint-disable-next-line no-magic-numbers
        }, 10000) // 10 second interval
    }

    const handleOnDismiss = () => {
        logAction(`error`, errorPopupMessageTemplate(dialogErrorMessage))
        navigate(Route.ROOT)
    }

    const handleSubmit = async () => {
        trackButtonClick(GtmEventName.PAYMENT_CLICK)

        if (!consent) {
            onConsentFalse()
            return
        }
        if (!stripe || !elements) {
            return
        }

        setIsLoading(true)

        const confirmPayment = async (stripeFunction: Promise<PaymentIntentResult | SetupIntentResult>) => {
            try {
                const res = await stripeFunction

                if (res?.error) {
                    setIsLoading(false)
                    setIsErrorDialogOpen(true)
                    setDialogErrorMessage(`${res.error.message}. The Stripe element has returned error: ${res.error.code} Error code: ${generateErrorCode()}`)
                    setDialogErrorMessageTitle(t(`sign_up_failure`) + `: ${res.error.type}`)
                    logAction(`error`, JSON.stringify(res.error))
                    return
                }
                if (isBundlePurchase || isSubscriptionGiftPurchase) {
                    // Get payment intent ID from the response
                    const paymentIntent = `paymentIntent` in res ? res.paymentIntent : undefined
                    const paymentIntentId = paymentIntent?.id || stripePaymentIntentId

                    if (paymentIntentId) {
                        startPaymentStatusPolling(paymentIntentId)
                    }
                } else {
                    onSuccess()
                }
            } catch (error: unknown) {
                setIsLoading(false)
                setIsErrorDialogOpen(true)
                setDialogErrorMessage(`${error} error code: ${generateErrorCode()}`)
                setDialogErrorMessageTitle(t(`stripe_payment_failure`))
                logAction(`error`, JSON.stringify(error))
            }
        }

        if (isFirstYearFree) {
            return confirmPayment(stripe.confirmSetup({
                elements,
                redirect: `if_required`,
                confirmParams: {
                    payment_method_data: {
                        billing_details: {
                            email,
                        },
                    },
                },
            }))
        } else {
            return confirmPayment(stripe.confirmPayment({
                elements,
                redirect: `if_required`,
                confirmParams: {
                    payment_method_data: {
                        billing_details: {
                            email,
                        },
                    },
                },
            }))
        }
    }

    const options: StripePaymentElementOptions = {
        layout: `tabs`,
        business: { name: `Stripe` },
    }

    let buttonLabel

    if (isFirstYearFree && !isSubscriptionGiftPurchase && !isSubscriptionGiftRedemption) {
        buttonLabel = t(`stripe_proceed_to_setup_card_payment`)
    }else if (isSubscriptionGiftPurchase){
        buttonLabel = t(`stripe_proceed_to_pay_subscription_gift`)
    }else if (isSubscriptionGiftRedemption){
        buttonLabel = t(`stripe_confirm_and_proceed_subscription_gift`)
    }else if (isBundlePurchase){
        buttonLabel = t(`stripe_proceed_to_pay_bundle`)
    }
    else {
        buttonLabel = t(`stripe_proceed_to_pay`)
    }
    const isButtonDisabled = !stripe || !elements || isLoading

    return (
        <Box className={styles.container}>
            <PaymentElement id="payment-element" options={options} />
            <LoadingButton variant='contained' className={styles.paymentButton} disabled={isButtonDisabled} onClick={handleSubmit} loading={isLoading} fullWidth>{buttonLabel}</LoadingButton>
            <ConfirmationDialog
                buttonText={t(`common_button_confirmation`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isErrorDialogOpen}
                onConfirm={handleErrorDialogClose}
                onDismiss={handleOnDismiss}
            />
        </Box>
    )
}